<template>
  <div class="login-container">
    <!-- ✅ BACKGROUND INDUSTRIAL COM AGULHAS E LINHA -->
    <div class="industrial-background">
      <!-- Agulhas -->
      <div class="needles-container">
        <div class="needle needle-1">
          <div class="needle-body"></div>
          <div class="needle-eye"></div>
        </div>
        <div class="needle needle-2">
          <div class="needle-body"></div>
          <div class="needle-eye"></div>
        </div>
        <div class="needle needle-3">
          <div class="needle-body"></div>
          <div class="needle-eye"></div>
        </div>
        <div class="needle needle-4">
          <div class="needle-body"></div>
          <div class="needle-eye"></div>
        </div>
      </div>

      <!-- Linha passando pelas agulhas -->
      <div class="thread-container">
        <div class="thread thread-1"></div>
        <div class="thread thread-2"></div>
        <div class="thread thread-3"></div>
      </div>

      <!-- Engrenagens industriais -->
      <div class="gears-container">
        <div class="gear gear-1"></div>
        <div class="gear gear-2"></div>
        <div class="gear gear-3"></div>
      </div>
    </div>

    <!-- ✅ DIALOG DE ERRO INTELIGENTE COM MENSAGENS AMIGÁVEIS -->
    <v-dialog v-model="state.isDialogActive" max-width="480" persistent>
      <v-card class="error-dialog" rounded="lg">
        <v-card-title class="error-header pa-4" :class="errorConfig.headerClass">
          <v-avatar size="48" :color="errorConfig.color" variant="tonal" class="mr-3">
            <v-icon :color="errorConfig.color" size="28">{{ errorConfig.icon }}</v-icon>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold">{{ errorConfig.title }}</div>
            <div class="text-caption text-grey">{{ errorConfig.subtitle }}</div>
          </div>
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <div class="error-content">
            <p class="text-body-1 mb-3">
              <strong>{{ errorConfig.message }}</strong>
            </p>
            <p class="text-body-2 text-grey-darken-1 mb-3">
              {{ errorConfig.description }}
            </p>

            <!-- ✅ DICA PARA O USUÁRIO -->
            <v-alert :type="errorConfig.alertType" variant="tonal" density="compact" class="mb-3"
              v-if="errorConfig.tip">
              <v-icon start>{{ errorConfig.alertIcon }}</v-icon>
              <strong>{{ errorConfig.tipTitle }}:</strong> {{ errorConfig.tip }}
            </v-alert>

            <!-- ✅ INFORMAÇÕES TÉCNICAS (APENAS EM DESENVOLVIMENTO) -->
            <v-card v-if="showTechnicalInfo" variant="outlined" color="grey-lighten-4" class="mb-3 pa-3">
              <div class="d-flex align-center mb-2 text-blue">
                <v-icon color="blue" class="mr-2">mdi-bug</v-icon>
                <span class="font-weight-bold text-caption">DEBUG INFO</span>
              </div>
              <div class="text-caption text-blue">
                <div><strong>Código:</strong> {{ state.errorCode }}</div>
                <div><strong>Técnico:</strong> {{ state.technicalMessage }}</div>
                <div v-if="state.timeInfo">
                  <strong>Horário:</strong> {{ state.timeInfo.dia }} às {{ state.timeInfo.hora }}
                </div>
              </div>
            </v-card>

            <!-- ✅ INFORMAÇÕES DE SUPORTE -->
            <v-card v-if="errorConfig.showSupport" variant="outlined" color="blue-lighten-5" class="mb-3 pa-3">
              <div class="d-flex align-center mb-2">
                <v-icon color="blue" class="mr-2">mdi-headset</v-icon>
                <span class="font-weight-bold text-body-2">Precisa de ajuda?</span>
              </div>
              <div class="text-body-2">
                Entre em contato com o suporte técnico se o problema persistir.
              </div>
            </v-card>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn :color="errorConfig.color" variant="flat" @click="closeErrorDialog" class="px-6">
            <v-icon start>{{ errorConfig.buttonIcon }}</v-icon>
            {{ errorConfig.buttonText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ✅ CONTAINER PRINCIPAL CENTRALIZADO -->
    <div class="login-wrapper">
      <div class="login-content-centered">
        <!-- ✅ LOGO COMPACTA - LADO A LADO -->
        <div class="logo-header">
          <div class="logo-row">
            <v-avatar :size="logoSize" class="logo-avatar">
              <v-icon color="white" :size="iconSize">mdi-factory</v-icon>
            </v-avatar>
            <div class="logo-text">
              <h1 class="app-title">JODARC</h1>
              <p class="app-subtitle">Sistema de Produção</p>
            </div>
          </div>
        </div>

        <!-- Card de login -->
        <v-card class="login-card" elevation="24">
          <div class="card-header">
            <h3 class="login-title">Bem-vindo de volta!</h3>
            <p class="login-subtitle">Faça login para acessar o sistema</p>
          </div>

          <v-card-text class="card-content">
            <v-form @submit.prevent="realizarLogin">
              <!-- Campo usuário -->
              <div class="input-group">
                <label class="input-label">Usuário</label>
                <v-text-field v-model="state.login" variant="outlined" density="compact"
                  prepend-inner-icon="mdi-account-circle" placeholder="Digite seu usuário" class="custom-input"
                  autocomplete="username"
                  :disabled="state.loading" :error="state.hasError"></v-text-field>
              </div>

              <!-- Campo senha -->
              <div class="input-group">
                <label class="input-label">Senha</label>
                <v-text-field v-model="state.senha" variant="outlined" density="compact" prepend-inner-icon="mdi-lock"
                  :append-inner-icon="state.show1 ? 'mdi-eye' : 'mdi-eye-off'" :type="state.show1 ? 'text' : 'password'"
                  placeholder="Digite sua senha" class="custom-input" :disabled="state.loading" :error="state.hasError"
                  autocomplete="current-password"
                  @click:append-inner="state.show1 = !state.show1" @keyup.enter="realizarLogin"></v-text-field>
              </div>

              <!-- Botões -->
              <div class="buttons-container">
                <v-btn variant="outlined" color="grey-darken-1" :size="buttonSize" class="action-btn clear-btn"
                  :disabled="state.loading" @click="limpar">
                  <v-icon start size="18">mdi-refresh</v-icon>
                  Limpar
                </v-btn>

                <v-btn color="primary" :size="buttonSize" class="action-btn login-btn" :loading="state.loading"
                  @click="realizarLogin">
                  <v-icon start size="18">mdi-login</v-icon>
                  Acessar Sistema
                </v-btn>
              </div>
            </v-form>
          </v-card-text>

          <!-- Footer do card -->
          <div class="card-footer">
            <div class="version-info">
              <v-icon size="16" class="mr-1">mdi-information-outline</v-icon>
              Versão {{ APP_VERSION }} • {{ APP_DATE }}
            </div>
            <div class="company-footer">
              Desenvolvido por Tabosa Sistemas 2026...
            </div>
          </div>
        </v-card>

        <!-- ✅ BADGES DE SEGURANÇA -->
        <div class="security-badges">
          <v-chip color="success" size="small">
            <v-icon start size="16">mdi-shield-check</v-icon>
            Conexão Segura
          </v-chip>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";
import { useUserStore } from "@/store/userStore";
import { useUserVendaStore } from "@/store/userStoreVenda";
import { registerSW } from 'virtual:pwa-register';
import { reactive, onMounted, computed } from "vue";

const useStoreVenda = useUserVendaStore();
const APP_VERSION = __APP_VERSION__;
const APP_DATE = __APP_DATE__;
const router = useRouter();
const userStore = useUserStore();

// ✅ VERIFICAR SE ESTÁ EM DESENVOLVIMENTO
const isDevelopment = computed(() => {
  return import.meta.env.DEV || import.meta.env.MODE === 'development';
});

const updateSW = registerSW({
  onNeedRefresh() {
    state.isDialogActive = true;
  },
  onOfflineReady() {
    // Sistema disponível offline
  },
});

const state = reactive({
  login: "",
  senha: "",
  show1: false,
  loading: false,
  isDialogActive: false,
  hasError: false,
  errorMessage: "",
  errorCode: "",
  technicalMessage: "",
  timeInfo: null,
});

// ✅ CONFIGURAÇÕES DE ERRO COM MENSAGENS AMIGÁVEIS
const errorConfig = computed(() => {
  const configs = {
    // Erros de horário - MENSAGENS AMIGÁVEIS
    'TIME_001': {
      title: 'Sistema Indisponível',
      subtitle: 'Manutenção temporária',
      message: 'O sistema está temporariamente indisponível.',
      description: 'Estamos realizando manutenção no sistema. Tente acessar novamente em alguns minutos. Se precisar de acesso urgente, entre em contato com seu supervisor.',
      icon: 'mdi-wrench',
      color: 'warning',
      alertType: 'info',
      alertIcon: 'mdi-information',
      tipTitle: 'Dica',
      tip: 'Aguarde alguns minutos e tente novamente',
      buttonText: 'Entendi',
      buttonIcon: 'mdi-check',
      headerClass: 'warning-header',
      showSupport: false
    },
    'TIME_002': {
      title: 'Sessão Expirada',
      subtitle: 'Manutenção do sistema',
      message: 'Sua sessão foi encerrada durante a manutenção.',
      description: 'O sistema passou por uma manutenção e sua sessão foi encerrada por segurança. Tente fazer login novamente.',
      icon: 'mdi-refresh',
      color: 'info',
      alertType: 'info',
      alertIcon: 'mdi-information',
      tipTitle: 'Ação necessária',
      tip: 'Faça login novamente',
      buttonText: 'Fazer Login',
      buttonIcon: 'mdi-login',
      headerClass: 'info-header',
      showSupport: false
    },
    'TIME_003': {
      title: 'Sistema em Manutenção',
      subtitle: 'Serviço temporariamente indisponível',
      message: 'Não é possível renovar sua sessão no momento.',
      description: 'O sistema está em manutenção. Aguarde alguns minutos e faça login novamente.',
      icon: 'mdi-tools',
      color: 'warning',
      alertType: 'warning',
      alertIcon: 'mdi-clock-alert',
      tipTitle: 'Aguarde',
      tip: 'Tente novamente em alguns minutos',
      buttonText: 'Entendi',
      buttonIcon: 'mdi-check',
      headerClass: 'warning-header',
      showSupport: false
    },

    // Erros de credenciais - MENSAGENS TÉCNICAS NORMAIS
    'USER_001': {
      title: 'Dados Incorretos',
      subtitle: 'Erro de autenticação',
      message: 'Usuário ou senha incorretos.',
      description: 'Verifique se digitou corretamente seu usuário e senha. Se o problema persistir, entre em contato com o suporte.',
      icon: 'mdi-account-alert',
      color: 'error',
      alertType: 'info',
      alertIcon: 'mdi-lightbulb-outline',
      tipTitle: 'Dica',
      tip: 'Verifique se o nome de usuário está correto',
      buttonText: 'Tentar Novamente',
      buttonIcon: 'mdi-refresh',
      headerClass: 'error-header',
      showSupport: true
    },
    'USER_002': {
      title: 'Acesso Restrito',
      subtitle: 'Conta não autorizada',
      message: 'Sua conta não tem permissão de acesso.',
      description: 'Entre em contato com o administrador do sistema para verificar suas permissões de acesso.',
      icon: 'mdi-account-off',
      color: 'error',
      alertType: 'warning',
      alertIcon: 'mdi-account-supervisor',
      tipTitle: 'Ação necessária',
      tip: 'Entre em contato com o administrador do sistema',
      buttonText: 'Entendi',
      buttonIcon: 'mdi-check',
      headerClass: 'error-header',
      showSupport: true
    },
    'PASS_001': {
      title: 'Dados Incorretos',
      subtitle: 'Erro de autenticação',
      message: 'Usuário ou senha incorretos.',
      description: 'Verifique se a senha foi digitada corretamente. Certifique-se de que o Caps Lock esteja desativado.',
      icon: 'mdi-lock-alert',
      color: 'error',
      alertType: 'info',
      alertIcon: 'mdi-keyboard-caps',
      tipTitle: 'Dica',
      tip: 'Verifique se o Caps Lock está desativado',
      buttonText: 'Tentar Novamente',
      buttonIcon: 'mdi-refresh',
      headerClass: 'error-header',
      showSupport: false
    },

    // Erros de segurança
    'SEC_001': {
      title: 'Muitas Tentativas',
      subtitle: 'Bloqueio de segurança',
      message: 'Muitas tentativas de acesso detectadas.',
      description: 'Por segurança, o acesso foi temporariamente bloqueado. Aguarde alguns minutos antes de tentar novamente.',
      icon: 'mdi-shield-alert',
      color: 'warning',
      alertType: 'warning',
      alertIcon: 'mdi-timer',
      tipTitle: 'Aguarde',
      tip: 'Espere 15 minutos antes de tentar novamente',
      buttonText: 'Entendi',
      buttonIcon: 'mdi-check',
      headerClass: 'warning-header',
      showSupport: false
    },

    // Erros de sistema - MENSAGENS AMIGÁVEIS
    'SYS_001': {
      title: 'Falha Temporária',
      subtitle: 'Problema técnico',
      message: 'Ocorreu uma falha temporária no sistema.',
      description: 'Tente novamente em alguns instantes. Se o problema persistir, entre em contato com o suporte técnico.',
      icon: 'mdi-alert-circle',
      color: 'error',
      alertType: 'error',
      alertIcon: 'mdi-phone',
      tipTitle: 'Suporte',
      tip: 'Entre em contato com o suporte se o erro persistir',
      buttonText: 'Tentar Novamente',
      buttonIcon: 'mdi-refresh',
      headerClass: 'error-header',
      showSupport: true
    },
    'SYS_002': {
      title: 'Erro de Conexão',
      subtitle: 'Problema de rede',
      message: 'Falha na comunicação com o servidor.',
      description: 'Verifique sua conexão com a internet e tente novamente. Se o problema persistir, entre em contato com o suporte.',
      icon: 'mdi-wifi-off',
      color: 'error',
      alertType: 'warning',
      alertIcon: 'mdi-network',
      tipTitle: 'Verificar',
      tip: 'Confirme se sua internet está funcionando',
      buttonText: 'Tentar Novamente',
      buttonIcon: 'mdi-refresh',
      headerClass: 'error-header',
      showSupport: true
    },

    // Erros de token - MENSAGENS AMIGÁVEIS
    'TOKEN_001': {
      title: 'Sessão Expirada',
      subtitle: 'Segurança do sistema',
      message: 'Sua sessão expirou por segurança.',
      description: 'Por motivos de segurança, sua sessão foi encerrada automaticamente. Faça login novamente para continuar.',
      icon: 'mdi-shield-check',
      color: 'info',
      alertType: 'info',
      alertIcon: 'mdi-shield-check',
      tipTitle: 'Segurança',
      tip: 'Isso acontece para proteger sua conta',
      buttonText: 'Fazer Login',
      buttonIcon: 'mdi-login',
      headerClass: 'info-header',
      showSupport: false
    },
    'TOKEN_002': {
      title: 'Sessão Inválida',
      subtitle: 'Erro de autenticação',
      message: 'Sua sessão não é mais válida.',
      description: 'Faça login novamente para acessar o sistema.',
      icon: 'mdi-key-remove',
      color: 'warning',
      alertType: 'info',
      alertIcon: 'mdi-information',
      tipTitle: 'Ação necessária',
      tip: 'Faça login para continuar',
      buttonText: 'Fazer Login',
      buttonIcon: 'mdi-login',
      headerClass: 'warning-header',
      showSupport: false
    },
    'TOKEN_003': {
      title: 'Renovação Falhou',
      subtitle: 'Erro de sessão',
      message: 'Não foi possível renovar sua sessão.',
      description: 'Faça login novamente para continuar usando o sistema.',
      icon: 'mdi-refresh-circle',
      color: 'warning',
      alertType: 'info',
      alertIcon: 'mdi-information',
      tipTitle: 'Solução',
      tip: 'Faça um novo login',
      buttonText: 'Fazer Login',
      buttonIcon: 'mdi-login',
      headerClass: 'warning-header',
      showSupport: false
    },

    // Erro padrão
    'default': {
      title: 'Falha no Acesso',
      subtitle: 'Problema temporário',
      message: 'Não foi possível acessar o sistema.',
      description: 'Ocorreu um problema temporário. Tente novamente em alguns instantes ou entre em contato com o suporte.',
      icon: 'mdi-alert-circle',
      color: 'error',
      alertType: 'info',
      alertIcon: 'mdi-lightbulb-outline',
      tipTitle: 'Dica',
      tip: 'Aguarde alguns minutos e tente novamente',
      buttonText: 'Tentar Novamente',
      buttonIcon: 'mdi-refresh',
      headerClass: 'error-header',
      showSupport: true
    }
  };

  return configs[state.errorCode] || configs['default'];
});

// ✅ FUNÇÃO PARA MOSTRAR INFORMAÇÕES TÉCNICAS (APENAS EM DEV)
const showTechnicalInfo = computed(() => {
  return isDevelopment.value && (state.technicalMessage || state.errorCode);
});

// ✅ TAMANHOS RESPONSIVOS
const logoSize = computed(() => {
  if (typeof window !== 'undefined') {
    if (window.innerWidth >= 1024) return 70; // Desktop
    if (window.innerWidth >= 768) return 60;  // Tablet
    return 50; // Mobile
  }
  return 60;
});

const iconSize = computed(() => {
  if (typeof window !== 'undefined') {
    if (window.innerWidth >= 1024) return 36; // Desktop
    if (window.innerWidth >= 768) return 32;  // Tablet
    return 28; // Mobile
  }
  return 32;
});

const buttonSize = computed(() => {
  if (typeof window !== 'undefined') {
    if (window.innerWidth <= 767) return 'large'; // Mobile
    return 'default'; // Desktop/Tablet
  }
  return 'default';
});

onMounted(() => {
  userStore.resetState();
  useStoreVenda.resetState();
});



const realizarLogin = async () => {
  try {
    state.loading = true;
    state.hasError = false;

    console.log('🚀 === INÍCIO LOGIN ===');
    console.log('👤 Usuário:', state.login);

    // ✨ CORRIGIDO: Aguardar o resultado do login
    const loginResult = await userStore.login(state.login, state.senha);

    // console.log('📡 === RESPOSTA DO STORE ===');
    // console.log('📡 Tipo da resposta:', typeof loginResult);
    // console.log('📡 Resposta completa:', loginResult);
    // console.log('📡 Resposta JSON:', JSON.stringify(loginResult, null, 2));

    // ✨ CORRIGIDO: Verificar a estrutura correta da resposta
    if (loginResult && loginResult.success === true) {
      // console.log('✅ Login bem-sucedido, redirecionando...');

      // Redirecionar para o menu
      router.push({ name: "MenuDefault" }).catch(() => { });

      // Atualizar PWA se disponível
      if (updateSW && updateSW.update) {
        updateSW.update();
      }
    } else {
      // ✅ NOVO: Processar erro com código
      console.log('❌ Login falhou, processando erro...');

      // ✅ PASSAR O RESULTADO EXATAMENTE COMO VEIO
      handleLoginError(loginResult);
    }
  } catch (error) {
    console.error("💥 === ERRO CATCH ===");
    console.error("💥 Erro completo:", error);
    console.error("💥 Error response:", error.response);
    console.error("💥 Error response data:", error.response?.data);

    // ✅ NOVO: Tratar erro de conexão
    let errorToHandle = {
      message: 'Erro de conexão com o servidor',
      error_code: 'SYS_002',
      technical_message: 'Erro de rede: ' + error.message
    };

    // ✅ VERIFICAR SE O ERRO TEM RESPONSE DATA
    if (error.response && error.response.data) {
      console.log('🔄 Usando dados do error.response.data');
      errorToHandle = error.response.data;
    }

    handleLoginError(errorToHandle);
  } finally {
    state.loading = false;
  }
};



// ✅ FUNÇÃO CORRIGIDA: Processar erros de login
const handleLoginError = (result) => {
  console.log('🔍 === INÍCIO DEBUG handleLoginError ===');
  console.log('📦 Resultado recebido (tipo):', typeof result);
  console.log('📦 Resultado recebido (valor):', result);
  console.log('📦 Resultado JSON:', JSON.stringify(result, null, 2));

  state.hasError = true;

  // ✅ EXTRAÇÃO ROBUSTA DO ERROR_CODE
  let errorCode = 'default';
  let errorMessage = 'Erro no login';
  let technicalMessage = 'Erro desconhecido';

  // Verificar múltiplas possibilidades de estrutura
  if (result) {
    // Caso 1: error_code diretamente no result
    if (result.error_code) {
      errorCode = result.error_code;
      console.log('✅ Error code encontrado diretamente:', errorCode);
    }
    // Caso 2: error_code dentro de result.data
    else if (result.data && result.data.error_code) {
      errorCode = result.data.error_code;
      console.log('✅ Error code encontrado em data:', errorCode);
    }
    // Caso 3: error_code dentro de result.response.data
    else if (result.response && result.response.data && result.response.data.error_code) {
      errorCode = result.response.data.error_code;
      console.log('✅ Error code encontrado em response.data:', errorCode);
    }
    else {
      console.log('❌ Error code NÃO encontrado, usando default');
    }

    // Extrair mensagem
    if (result.message) {
      errorMessage = result.message;
      technicalMessage = result.message;
    } else if (result.data && result.data.message) {
      errorMessage = result.data.message;
      technicalMessage = result.data.message;
    } else if (result.response && result.response.data && result.response.data.message) {
      errorMessage = result.response.data.message;
      technicalMessage = result.response.data.message;
    }
  }

  // Atribuir aos estados
  state.errorCode = errorCode;
  state.errorMessage = errorMessage;
  state.technicalMessage = technicalMessage;

  console.log('🎯 === RESULTADO FINAL ===');
  console.log('🎯 Error code final:', state.errorCode);
  console.log('🎯 Error message final:', state.errorMessage);
  console.log('🎯 Technical message final:', state.technicalMessage);
  console.log('🔍 === FIM DEBUG handleLoginError ===');

  // ✅ EXTRAIR INFORMAÇÕES DE HORÁRIO SE DISPONÍVEL
  const details = result.details || (result.data && result.data.details) || (result.response && result.response.data && result.response.data.details);
  if (details && details.dia && details.hora_tentativa) {
    state.timeInfo = {
      dia: details.dia,
      hora: details.hora_tentativa
    };
  }

  showDialog();
};


const limpar = () => {
  state.login = "";
  state.senha = "";
  state.hasError = false;
  state.errorCode = "";
  state.technicalMessage = "";
  state.timeInfo = null;
};

const showDialog = () => {
  state.isDialogActive = true;
};

// ✅ NOVA FUNÇÃO: Fechar dialog com ações específicas
const closeErrorDialog = () => {
  state.isDialogActive = false;

  // ✅ AÇÕES ESPECÍFICAS BASEADAS NO TIPO DE ERRO
  if (state.errorCode.startsWith('TIME_')) {
    // Para erros de horário, limpar campos
    limpar();
  } else if (state.errorCode === 'PASS_001') {
    // Para senha incorreta, limpar apenas a senha
    state.senha = "";
    state.hasError = false;
  } else if (state.errorCode === 'USER_001') {
    // Para usuário não encontrado, focar no campo usuário
    state.hasError = false;
  } else if (state.errorCode === 'SEC_001') {
    // Para muitas tentativas, limpar tudo
    limpar();
  }

  // Resetar estado do erro
  state.errorCode = "";
  state.technicalMessage = "";
  state.timeInfo = null;
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #2c3e50 100%);
}

/* ✅ BACKGROUND INDUSTRIAL COM AGULHAS E LINHA */
/* ✅ BACKGROUND INDUSTRIAL COM AGULHAS E LINHA */
.industrial-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
}

/* Agulhas */
.needles-container {
  position: absolute;
  width: 100%;
  height: 100%;
}

.needle {
  position: absolute;
  animation: needle-float 4s ease-in-out infinite;
}

.needle-body {
  width: 3px;
  height: 80px;
  background: linear-gradient(to bottom, #bdc3c7, #95a5a6);
  border-radius: 2px;
  position: relative;
}

.needle-body::before {
  content: '';
  position: absolute;
  top: -8px;
  left: -2px;
  width: 7px;
  height: 8px;
  background: #7f8c8d;
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
}

.needle-eye {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 8px;
  height: 12px;
  border: 2px solid #7f8c8d;
  border-radius: 50%;
  background: transparent;
}

.needle-1 {
  top: 15%;
  left: 10%;
  animation-delay: 0s;
}

.needle-2 {
  top: 25%;
  right: 15%;
  animation-delay: 1s;
  transform: rotate(45deg);
}

.needle-3 {
  bottom: 30%;
  left: 20%;
  animation-delay: 2s;
  transform: rotate(-30deg);
}

.needle-4 {
  bottom: 20%;
  right: 25%;
  animation-delay: 3s;
  transform: rotate(60deg);
}

@keyframes needle-float {

  0%,
  100% {
    transform: translateY(0px) rotate(var(--rotation, 0deg));
  }

  50% {
    transform: translateY(-15px) rotate(var(--rotation, 0deg));
  }
}

/* Linha passando pelas agulhas */
.thread-container {
  position: absolute;
  width: 100%;
  height: 100%;
}

.thread {
  position: absolute;
  height: 2px;
  background: linear-gradient(90deg,
      transparent 0%,
      #e74c3c 10%,
      #c0392b 50%,
      #e74c3c 90%,
      transparent 100%);
  animation: thread-move 6s linear infinite;
}

.thread-1 {
  top: 20%;
  width: 60%;
  left: 5%;
  animation-delay: 0s;
}

.thread-2 {
  top: 50%;
  width: 70%;
  right: 5%;
  animation-delay: 2s;
}

.thread-3 {
  bottom: 25%;
  width: 50%;
  left: 15%;
  animation-delay: 4s;
}

@keyframes thread-move {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  90% {
    opacity: 1;
  }

  100% {
    transform: translateX(100%);
    opacity: 0;
  }
}

/* Engrenagens industriais */
.gears-container {
  position: absolute;
  width: 100%;
  height: 100%;
}

.gear {
  position: absolute;
  border: 4px solid rgba(149, 165, 166, 0.3);
  border-radius: 50%;
  animation: gear-rotate 8s linear infinite;
}

.gear::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60%;
  height: 60%;
  border: 2px solid rgba(149, 165, 166, 0.2);
  border-radius: 50%;
}

.gear-1 {
  width: 60px;
  height: 60px;
  top: 10%;
  right: 10%;
  animation-duration: 6s;
}

.gear-2 {
  width: 80px;
  height: 80px;
  bottom: 15%;
  left: 8%;
  animation-duration: 8s;
  animation-direction: reverse;
}

.gear-3 {
  width: 40px;
  height: 40px;
  top: 60%;
  right: 20%;
  animation-duration: 4s;
}

@keyframes gear-rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* ✅ WRAPPER CENTRALIZADO */
.login-wrapper {
  position: relative;
  z-index: 2;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-content-centered {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ✅ LOGO HEADER */
.logo-header {
  margin-bottom: 32px;
  animation: fadeInDown 0.8s ease-out;
}

.logo-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.logo-avatar {
  background: linear-gradient(135deg, #3498db, #2980b9);
  border: 3px solid rgba(255, 255, 255, 0.3);
  flex-shrink: 0;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.logo-text {
  text-align: left;
}

.app-title {
  font-weight: 700;
  color: white;
  margin: 0;
  line-height: 1;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
}

.app-subtitle {
  color: rgba(255, 255, 255, 0.9);
  margin: 4px 0 0 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
}

/* ✅ FORMULÁRIO DE LOGIN */
.login-card {
  width: 100%;
  border-radius: 20px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  animation: slideInUp 0.6s ease-out;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.card-header {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  color: white;
  padding: 24px 24px 20px 24px;
  text-align: center;
}

.login-title {
  font-weight: 600;
  margin: 0 0 6px 0;
}

.login-subtitle {
  opacity: 0.9;
  margin: 0;
}

.card-content {
  padding: 24px;
}

.input-group {
  margin-bottom: 16px;
}

.input-label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  font-size: 0.875rem;
}

.custom-input {
  border-radius: 12px;
}

.custom-input :deep(.v-field) {
  border-radius: 12px !important;
}

.buttons-container {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}

.action-btn {
  flex: 1;
  border-radius: 12px;
  font-weight: 600;
  text-transform: none;
  font-size: 0.9rem;
  min-height: 44px;
}

.login-btn {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%) !important;
}

.card-footer {
  background: #f8f9fa;
  padding: 16px 24px;
  text-align: center;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.version-info {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.company-footer {
  font-size: 0.75rem;
  color: #999;
}

.security-badges {
  margin-top: 20px;
}

/* ✅ DIALOG DE ERRO */
.error-dialog {
  overflow: hidden;
}

.error-header {
  background: linear-gradient(135deg, #ffebee 0%, #fce4ec 100%);
}

.warning-header {
  background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%);
}

.info-header {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
}

.error-content {
  text-align: left;
}

/* ✅ RESPONSIVIDADE */

/* Desktop (1024px+) */
@media (min-width: 1024px) {
  .login-wrapper {
    padding: 40px;
  }

  .login-content-centered {
    max-width: 450px;
  }

  .logo-header {
    margin-bottom: 40px;
  }

  .logo-row {
    gap: 20px;
  }

  .app-title {
    font-size: 3rem;
  }

  .app-subtitle {
    font-size: 1.1rem;
  }

  .login-title {
    font-size: 1.6rem;
  }

  .login-subtitle {
    font-size: 1rem;
  }

  .card-content {
    padding: 32px;
  }

  .action-btn {
    min-height: 48px;
    font-size: 1rem;
  }
}

/* Tablet (768px - 1023px) */
@media (min-width: 768px) and (max-width: 1023px) {
  .login-wrapper {
    padding: 32px;
  }

  .login-content-centered {
    max-width: 400px;
  }

  .logo-header {
    margin-bottom: 36px;
  }

  .logo-row {
    gap: 18px;
  }

  .app-title {
    font-size: 2.5rem;
  }

  .app-subtitle {
    font-size: 1rem;
  }

  .login-title {
    font-size: 1.5rem;
  }

  .login-subtitle {
    font-size: 0.95rem;
  }

  .card-content {
    padding: 28px;
  }

  .action-btn {
    min-height: 46px;
  }
}

/* Mobile (até 767px) */
@media (max-width: 767px) {
  .login-wrapper {
    padding: 16px;
    align-items: flex-start;
    padding-top: 60px;
  }

  .login-content-centered {
    max-width: 100%;
  }

  .logo-header {
    margin-bottom: 24px;
  }

  .logo-row {
    gap: 12px;
  }

  .app-title {
    font-size: 2rem;
  }

  .app-subtitle {
    font-size: 0.9rem;
  }

  .login-title {
    font-size: 1.3rem;
  }

  .login-subtitle {
    font-size: 0.85rem;
  }

  .card-header {
    padding: 20px 20px 16px 20px;
  }

  .card-content {
    padding: 20px;
  }

  .input-group {
    margin-bottom: 14px;
  }

  .buttons-container {
    flex-direction: column;
    gap: 12px;
  }

  .action-btn {
    min-height: 52px;
    font-size: 1rem;
  }

  .card-footer {
    padding: 12px 16px;
  }
}

/* Mobile pequeno (até 480px) */
@media (max-width: 480px) {
  .login-wrapper {
    padding: 12px;
    padding-top: 40px;
  }

  .logo-row {
    gap: 10px;
  }

  .app-title {
    font-size: 1.75rem;
  }

  .card-header,
  .card-content {
    padding: 16px;
  }

  .card-footer {
    padding: 12px 16px;
  }

  .action-btn {
    min-height: 56px;
    font-size: 1.1rem;
  }
}

/* ✅ ANIMAÇÕES */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>